/** Worker-pure inland swamp cohorts. Candidates belong to finite shared
 * basins, not renderer tiles; hydrology decides every accepted depth. */
import {createSurfaceColumn,FACES,inlandWetlandDirection,inlandWetlandEdge,pmath,rngFromString,seaSurface,surfaceColumnFromSample,
  type InlandWetlandDef,type PlanetDef,type PlanetSampler,type TerrainSample} from '@hyperlight/shared';
import {hasLivingGround,groundSnow} from '../groundSurface';
type V3=readonly[number,number,number];
export type InlandWetlandKind='cypress'|'reeds'|'cover';
export interface InlandWetlandTile {face:number;level:number;u0:number;v0:number;size:number}
interface Candidate {id:string;featureId:string;kind:InlandWetlandKind;face:number;u:number;v:number;dir:V3;size:number;yaw:number;variant:number;priority:number}
export interface InlandWetlandPlacement extends Candidate {normal:V3;radius:number;waterRadius:number;depth:number;mask:number;mud:number;footprint:number;relief:number}
export type InlandWetlandData=InlandWetlandPlacement[];
export const INLAND_WETLAND_LIMITS={cypress:32,reeds:160,cover:96} as const;
export const INLAND_WETLAND_CANDIDATES={cypress:324,low:1996,hummockLow:396} as const;
export const INLAND_WETLAND_DEPTH={cypress:[-1.85,.56],reeds:[-.50,.42],cover:[-2.2,-.045]} as const;
const fresh=():TerrainSample=>({h:0,temp:0,moist:0,river:0,mountain:0,town:0,road:8,flowx:0,flowy:0,flowz:0});
const column=createSurfaceColumn(),cache=new WeakMap<InlandWetlandDef,readonly Candidate[]>();
const norm=(x:number,y:number,z:number):V3=>{const r=Math.sqrt(x*x+y*y+z*z);return[x/r,y/r,z/r];};
const smooth=(a:number,b:number,x:number):number=>{const k=Math.max(0,Math.min(1,(x-a)/(b-a)));return k*k*(3-2*k);};

/** Mask only the specific finite habitat, without another climate/noise query. */
export function inlandWetlandForestWeight(ts:TerrainSample):number {
  return smooth(.12,.80,ts.wetlandMask??0)*(1-smooth(.002,.03,ts.town));
}
export function inlandWetlandAllowed(kind:InlandWetlandKind,def:PlanetDef,A:number,ts:TerrainSample,footprint=0):boolean {
  if(!hasLivingGround(def)||def.atmosphere===null||(def.seaRadius!==null&&seaSurface(def)!=='water')
    ||(ts.wetlandMask??0)<.55||(ts.wetlandWaterM??-1e9)<-1e8||ts.town>.002||ts.road<3+footprint||ts.river>.15
    ||(ts.glacierIceM??0)>.02||(ts.lavaDepthM??0)>0||(ts.canyonRock??0)>.02||(ts.butteRock??0)>.02)return false;
  if(groundSnow(ts.temp,ts.h*A,def.climate.snowline*A)>.08)return false;
  const depth=surfaceColumnFromSample(def,A,ts,column).waterDepth,[lo,hi]=INLAND_WETLAND_DEPTH[kind];
  return depth>=lo&&depth<=hi;
}
/** Single cube-face owner, including exact seams. */
function faceUV(d:V3):{face:number;u:number;v:number} {
  let face=0,best=-Infinity;for(let f=0;f<6;f++){const n=FACES[f].normal,k=d[0]*n[0]+d[1]*n[1]+d[2]*n[2];if(k>best){face=f;best=k;}}
  const b=FACES[face];return{face,u:d.reduce((v,n,i)=>v+n*b.right[i],0)/best,v:d.reduce((v,n,i)=>v+n*b.up[i],0)/best};
}
function candidates(f:InlandWetlandDef,R:number):readonly Candidate[] {
  let list=cache.get(f);if(list)return list;
  const result:Candidate[]=[];
  for(const trees of [true,false]){
    const maximum=trees?18:40,spacing=trees?20:5.5,n=Math.max(1,Math.min(maximum,Math.ceil(f.radiusM*2/spacing))),step=f.radiusM*2/n;
    for(let j=0;j<n;j++)for(let i=0;i<n;i++){
      const id=`${f.id}:inland-habitat1:${trees?'tree':'low'}:${i}:${j}`,rng=rngFromString(`${f.seed}:${id}`);
      const u=-f.radiusM+(i+.15+rng()*.70)*step,v=-f.radiusM+(j+.15+rng()*.70)*step;
      if(u*u+v*v>f.radiusM*f.radiusM)continue;
      const keep=rng();if(keep<(trees?.28:.24))continue;
      const kind:InlandWetlandKind=trees?'cypress':rng()<.38?'cover':'reeds';
      const dir=inlandWetlandDirection(f,R,u,v),uv=faceUV(dir);
      result.push({id,featureId:f.id,kind,...uv,dir,size:trees?.86+rng()*.27:kind==='reeds'?.9+rng()*.5:.85+rng()*.65,
        yaw:rng()*Math.PI*2,variant:Math.floor(rng()*2),priority:rng()});
    }
  }
  // The broad lattice can miss an entire small dry island. Reserve compact
  // colonies on the shared hummock geometry: two three-shrub shoulder groups
  // and three four-reed edge groups. Their own streams leave every existing
  // tree candidate and tree-cap ordering unchanged. Actual support/depth,
  // channels and settlement exclusions still decide acceptance below.
  for(let h=0;h<Math.min(22,f.hummocks.length);h++){
    const hummock=f.hummocks[h];
    for(const kind of ['cover','reeds'] as const){
      const groups=kind==='cover'?2:3,members=kind==='cover'?3:4;
      for(let g=0;g<groups;g++){
        const colony=`${f.id}:inland-hummock1:${h}:${kind}:${g}`,anchor=rngFromString(`${f.seed}:${colony}`);
        const angle=anchor()*Math.PI*2,radial=kind==='cover'?.27+anchor()*.28:.83+anchor()*.15;
        const u=hummock.u+pmath.cos(angle)*hummock.halfU*radial,v=hummock.v+pmath.sin(angle)*hummock.halfV*radial;
        for(let m=0;m<members;m++){
          const id=`${colony}:${m}`,rng=rngFromString(`${f.seed}:${id}`),a=m*Math.PI*2/members+rng()*.55;
          const spread=Math.min(kind==='cover'?2.1:2.5,Math.min(hummock.halfU,hummock.halfV)*.14),offset=spread*(.65+rng()*.35);
          const cu=u+pmath.cos(a)*offset,cv=v+pmath.sin(a)*offset;if(cu*cu+cv*cv>=f.radiusM*f.radiusM)continue;
          const dir=inlandWetlandDirection(f,R,cu,cv),uv=faceUV(dir);
          result.push({id,featureId:f.id,kind,...uv,dir,size:kind==='reeds'?.9+rng()*.5:.85+rng()*.65,
            yaw:rng()*Math.PI*2,variant:Math.floor(rng()*2),priority:-1+rng()*.2});
        }
      }
    }
  }
  result.sort((a,b)=>a.priority-b.priority||(a.id<b.id?-1:a.id>b.id?1:0));cache.set(f,result);return result;
}
const PROBES:readonly (readonly[number,number])[]=[[1,0],[-1,0],[0,1],[0,-1],[Math.SQRT1_2,Math.SQRT1_2],[-Math.SQRT1_2,Math.SQRT1_2],[-Math.SQRT1_2,-Math.SQRT1_2],[Math.SQRT1_2,-Math.SQRT1_2]];

/** Rooted islands and bank stands frame open pools. This is a tree-only
 * habitat test against the existing basin plan, never another terrain field.
 * Clearance includes the whole root envelope so shallow channel shoulders
 * cannot grow a trunk across the channel's walkable / reflective corridor. */
function cypressMargin(f:InlandWetlandDef,R:number,p:InlandWetlandPlacement):boolean {
  const d=p.dir,dot=d[0]*f.center[0]+d[1]*f.center[1]+d[2]*f.center[2],scale=R/dot;
  const u=(d[0]*f.along[0]+d[1]*f.along[1]+d[2]*f.along[2])*scale;
  const v=(d[0]*f.across[0]+d[1]*f.across[1]+d[2]*f.across[2])*scale;
  for(const c of f.channels){
    const du=c.u1-c.u0,dv=c.v1-c.v0,den=du*du+dv*dv;
    const t=den>0?Math.max(0,Math.min(1,((u-c.u0)*du+(v-c.v0)*dv)/den)):0;
    const x=u-c.u0-t*du,y=v-c.v0-t*dv,clearance=c.halfM+p.footprint;
    if(x*x+y*y<clearance*clearance)return false;
  }
  if(inlandWetlandEdge(f,u,v)<=24)return true;
  return f.hummocks.some(h=>{
    const x=(u-h.u)/h.halfU,y=(v-h.v)/h.halfV;
    return x*x+y*y<=1.8*1.8;
  });
}

export function buildInlandWetlandData(def:PlanetDef,sampler:PlanetSampler,tile:InlandWetlandTile,treesOnly=false):InlandWetlandData {
  const result:InlandWetlandData=[];
  if(!hasLivingGround(def)||!def.atmosphere||(def.seaRadius!==null&&seaSurface(def)!=='water'))return result;
  const b=FACES[tile.face],u=tile.u0+tile.size*.5,v=tile.v0+tile.size*.5;
  const mid=norm(...b.normal.map((n,i)=>n+b.right[i]*u+b.up[i]*v) as [number,number,number]);
  const counts={cypress:0,reeds:0,cover:0},ts=fresh(),pt=fresh(),A=sampler.amplitude;
  for(const f of sampler.inlandWetlands()){
    const reach=def.radius*tile.size+f.radiusM+10;
    const dx=(f.center[0]-mid[0])*def.radius,dy=(f.center[1]-mid[1])*def.radius,dz=(f.center[2]-mid[2])*def.radius;
    if(dx*dx+dy*dy+dz*dz>reach*reach)continue;
    for(const c of candidates(f,def.radius)){
      if(treesOnly&&c.kind!=='cypress')continue;
      if(c.face!==tile.face||c.u<tile.u0||c.u>=tile.u0+tile.size||c.v<tile.v0||c.v>=tile.v0+tile.size||counts[c.kind]>=INLAND_WETLAND_LIMITS[c.kind])continue;
      sampler.sample(...c.dir,ts,true);
      const footprint=c.size*(c.kind==='cypress'?3.9:c.kind==='reeds'?1.0:1.25);
      if(!inlandWetlandAllowed(c.kind,def,A,ts,footprint))continue;
      const r=def.radius+ts.h*A,[x,y,z]=c.dir,ex=Math.abs(y)<.9?norm(-z,0,x):norm(0,z,-y),ez:V3=[y*ex[2]-z*ex[1],z*ex[0]-x*ex[2],x*ex[1]-y*ex[0]];
      let low=0,high=0,valid=true;const heights:number[]=[];
      for(let p=0;p<(c.kind==='cypress'?8:4);p++){
        const [a,b]=PROBES[p],d=norm(...c.dir.map((n,k)=>n+(ex[k]*a+ez[k]*b)*footprint/r) as [number,number,number]);
        sampler.sample(...d,pt,true);
        if(!inlandWetlandAllowed(c.kind,def,A,pt)){valid=false;break;}
        const delta=(pt.h-ts.h)*A;heights.push(delta);low=Math.min(low,delta);high=Math.max(high,delta);
      }
      if(!valid||high-low>(c.kind==='cypress'?.82:.38))continue;
      const gx=(heights[0]-heights[1])/(2*footprint),gz=(heights[2]-heights[3])/(2*footprint);
      const normal=c.kind==='cypress'?c.dir:norm(...c.dir.map((n,k)=>n-gx*ex[k]-gz*ez[k]) as [number,number,number]);
      const col=surfaceColumnFromSample(def,A,ts,column);
      const placement={...c,normal,radius:r+(c.kind==='cypress'?low:0)-.025,waterRadius:col.waterR,depth:col.waterDepth,
        mask:ts.wetlandMask??0,mud:ts.wetlandMud??0,footprint,relief:high-low};
      // Consume the original cap before thinning. A removed pool tree must
      // not backfill a different candidate or disturb the near/far cohort.
      counts[c.kind]++;
      if(c.kind!=='cypress'||cypressMargin(f,def.radius,placement))result.push(placement);
    }
  }
  return result;
}
/** Far tiles reproduce their canonical children's exact capped tree cohort. */
export function buildInlandWetlandFarData(def:PlanetDef,sampler:PlanetSampler,tile:InlandWetlandTile):InlandWetlandData {
  const result:InlandWetlandData=[],size=tile.size*.5;
  for(let j=0;j<2;j++)for(let i=0;i<2;i++)result.push(...buildInlandWetlandData(def,sampler,{face:tile.face,level:tile.level+1,u0:tile.u0+i*size,v0:tile.v0+j*size,size},true));
  return result;
}
